import styled from 'styled-components';
import React, {FC} from 'react';

import {
  fontSizesMixins,
  palette,
  mediaBreakpointUp,
  mediaBreakpointDown,
  fontWeights
} from '../../../../lib/styleVariables';
import {convertHexToRGBA} from '../../../../lib/color-utils';
import {PrimaryButton, SecondaryButton} from '../../../molecules/buttonsR4';
import {ImageProps, Image} from '../../../common/image';

export interface ImageTopTextDescButtonProps {
  description?: React.ReactNode;
  imgSrc: ImageProps['fluid'];
  primaryButtonLink: string;
  primaryButtonText: string;
  secondaryButtonLink?: string;
  secondaryButtonText?: string;
  title: React.ReactNode;
}

const StyledContainer = styled.div`
  background-color: ${palette.blue.highlight};
  border-radius: 10px;
  padding-bottom: 80px;
  padding-top: 72px;
  width: 100%;

  ${mediaBreakpointDown('tablet')} {
    padding-bottom: 35px;
    padding-top: 35px;
  }
`;

const StyledImageWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow: hidden;
  position: relative;

  &::before,
  &::after {
    background: linear-gradient(
      90deg,
      ${palette.blue.highlight},
      ${convertHexToRGBA(palette.blue.highlight, 0.01)}
    );
    content: '';
    height: 100%;
    position: absolute;
    top: 0;
    width: 15%;
    z-index: 1;
  }

  &::before {
    left: -1px;
  }

  &::after {
    right: -1px;
    transform: rotate(180deg);
  }
`;

const StyledImage = styled(Image)`
  display: block;

  ${mediaBreakpointDown('mobile')} {
    width: 170%;
    margin-left: -35%;
    margin-right: -35%;
  }

  ${mediaBreakpointUp('tablet')} {
    width: 100%;
  }
`;

const StyledContent = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  margin-left: auto;
  margin-right: auto;
  margin-top: 40px;
  max-width: 808px;
  text-align: center;

  ${mediaBreakpointDown('tablet')} {
    margin-top: 24px;
    padding-left: 8px;
    padding-right: 8px;
  }
`;

const StyledTitle = styled.h2`
  ${fontSizesMixins.h2()}
  margin: 0;

  ${mediaBreakpointDown('mobile')} {
    font-weight: ${fontWeights.normal};
  }
`;

const StyledDescription = styled.p`
  ${fontSizesMixins.text2()};
  margin-top: 16px;
  margin-bottom: 0;
`;

const StyledButtons = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 32px;

  ${mediaBreakpointDown('mobile')} {
    align-items: center;
    flex-direction: column;
    margin-top: 24px;
  }
`;

const buttonStyle = `
  ${mediaBreakpointDown('mobile')} {
    &:not(:first-child) {
      margin-top: 12px;
    }
  }

  ${mediaBreakpointUp('tablet')} {
    &:not(:last-child) {
      margin-right: 16px;
    }
  }
`;

const StyledPrimaryButton = styled(PrimaryButton)`
  ${buttonStyle}
`;

const StyledSecondaryButton = styled(SecondaryButton)`
  ${buttonStyle}
`;

export const ImageTopTextDescButton: FC<ImageTopTextDescButtonProps> = props => {
  const {
    description,
    imgSrc,
    primaryButtonLink,
    primaryButtonText,
    secondaryButtonLink,
    secondaryButtonText,
    title
  } = props;
  const hasSecondaryButton = secondaryButtonLink && secondaryButtonText;

  return (
    <StyledContainer>
      <StyledImageWrapper>
        <StyledImage fluid={imgSrc} />
      </StyledImageWrapper>

      <StyledContent>
        <StyledTitle>{title}</StyledTitle>
        {description && <StyledDescription>{description}</StyledDescription>}
        <StyledButtons>
          <StyledPrimaryButton transparentBackground href={primaryButtonLink}>{primaryButtonText}</StyledPrimaryButton>

          {hasSecondaryButton && (
            <StyledSecondaryButton transparentBackground href={secondaryButtonLink}>{secondaryButtonText}</StyledSecondaryButton>
          )}
        </StyledButtons>
      </StyledContent>
    </StyledContainer>
  );
};
